/**
 * "Add a service from an image" (Apps › + Add service › From a container image…): a broker, a search engine, a cache or
 * a database Flow does not run on its own, as a service of the project run with Docker in the sandbox (vfs-service
 * src/preview/imageServices.js). A picker of common images with sane defaults (RabbitMQ, OpenSearch, Memcached, NATS,
 * MySQL, …) or any image; the name, ports, variables, the folder to keep and the memory can be changed before adding.
 * The tag is pinned with its digest when the service is added. Where Docker is off, says why (this Flow, or the plan)
 * instead of the form. After adding: which variables the other services read to reach it.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import { addImageService, getImageCatalog } from '../../services/ImageServicesService';
import { bodyOf, formFor } from '../../utils/imageServiceModel';
import NoDockerNote from './NoDockerNote';

const FIELDS = [
  ['name', 'Service name', 'rabbitmq'],
  ['image', 'Image (name:tag)', 'redis:8.2'],
  ['port', 'Main port of the container', '5672 (empty: a worker with no port)'],
  ['ports', 'Other ports (name:port, /grpc or /tcp)', 'ui:15672'],
  ['volume', 'Folder to keep across restarts', '/data'],
  ['memory', 'Memory limit', '512m'],
];

export default function ImageServiceDialog({ projectId, open, onClose, onAdded = null, taken = [] }) {
  const [catalog, setCatalog] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open || !projectId) { return undefined; }
    let live = true;
    setDone(null); setError(null); setForm(null);
    getImageCatalog(projectId).then((data) => { if (live) { setCatalog(data); } }).catch((e) => { if (live) { setLoadError(e.message); } });
    return () => { live = false; };
  }, [open, projectId]);

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const add = async() => {
    setError(null);
    let body;
    try { body = bodyOf(form, taken); } catch (e) { setError(e.message); return; }
    setBusy(true);
    try {
      const made = await addImageService(projectId, body);
      setDone(made);
      onAdded?.(made);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const dockerOff = catalog && !catalog.docker;
  let content;
  if (loadError) {
    content = <p className="apps-muted" role="alert">Could not load the images: {loadError}</p>;
  } else if (!catalog) {
    content = <p className="apps-muted">Loading…</p>;
  } else if (dockerOff) {
    content = <p data-testid="image-service-docker-off">A service from an image runs with Docker. <NoDockerNote reason={catalog.dockerOff || null} /></p>;
  } else if (done) {
    content = (
      <div data-testid="image-service-done">
        <p>Added <strong>{done.service}</strong> ({done.spec?.image}). Start it from Apps.</p>
        {done.env?.length > 0 && <p>The other services reach it with {done.env.map((name) => <code key={name}>{name} </code>)}</p>}
        {done.connect && <p>Connect with <code>{done.connect}</code></p>}
        {done.note && <p className="apps-muted">{done.note}</p>}
      </div>
    );
  } else if (!form) {
    content = (
      <ul className="image-service-picker" data-testid="image-service-picker">
        {catalog.images.map((entry) => (
          <li key={entry.id}>
            <button className="apps-info-link" data-testid={`image-service-pick-${entry.id}`} onClick={() => setForm(formFor(entry, taken))} type="button">
              <strong>{entry.name}</strong> <span className="apps-muted">{entry.image}</span>
            </button>
            <small className="apps-muted"> {entry.description}</small>
          </li>
        ))}
        <li><button className="apps-info-link" data-testid="image-service-pick-any" onClick={() => setForm(formFor(null))} type="button"><strong>Any image…</strong></button></li>
      </ul>
    );
  } else {
    content = (
      <form className="image-service-form" onSubmit={(event) => { event.preventDefault(); add(); }}>
        {FIELDS.map(([key, label, placeholder]) => (
          <label key={key} style={{ display: 'block', marginBottom: 8 }}>
            {label}
            <input className="ant-input" data-testid={`image-service-${key}`} onChange={set(key)} placeholder={placeholder} value={form[key]} />
          </label>
        ))}
        <label style={{ display: 'block', marginBottom: 8 }}>
          Main port protocol
          <select className="ant-input" data-testid="image-service-protocol" onChange={set('protocol')} value={form.protocol}>
            <option value="http">HTTP (opens in the browser)</option>
            <option value="grpc">gRPC</option>
            <option value="tcp">TCP (a broker, a database)</option>
          </select>
        </label>
        <label style={{ display: 'block', marginBottom: 8 }}>
          Variables (NAME=value, one per line)
          <textarea className="ant-input" data-testid="image-service-env" onChange={set('env')} rows={4} value={form.env} />
        </label>
        {error && <p className="apps-bad" role="alert">{error}</p>}
        <button className="apps-info-link" onClick={() => setForm(null)} type="button">← Other images</button>
      </form>
    );
  }

  const canAdd = Boolean(form) && !done && !dockerOff;
  return (
    <Modal
      cancelText={done ? 'Close' : 'Cancel'}
      confirmLoading={busy}
      okButtonProps={{ disabled: !canAdd, 'data-testid': 'image-service-add', style: canAdd ? undefined : { display: 'none' } }}
      okText="Add service"
      onCancel={onClose}
      onOk={add}
      open={open}
      title="Add a service from an image"
    >
      {content}
    </Modal>
  );
}

ImageServiceDialog.propTypes = {
  onAdded: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  projectId: PropTypes.string,
  // The project's service names (short), so the new one gets a free name.
  taken: PropTypes.arrayOf(PropTypes.string),
};
